/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * Deferred header resources: mega menu content, mega menu CSS/JS, mobile menu CSS.
 */

// document.currentScript is null in ES modules (this file loads as type="module"),
// so fall back to selecting the script by its data attributes.
const script = document.currentScript || document.querySelector("script[data-mobile-menu-css]");
const MEGA_MENU_CSS_URL = script?.dataset?.megaMenuCss;
const MOBILE_MENU_CSS_URL = script?.dataset?.mobileMenuCss;
const MEGA_MENU_JS_URL = script?.dataset?.megaMenuJs;

const loadedStylesheets = new Set();
const loadedScripts = new Set();
const loadingMenus = new Map();

function extractSectionId(element) {
  const sectionElement = element?.classList?.contains("shopify-section")
    ? element
    : element?.closest?.(".shopify-section");

  if (sectionElement) {
    return sectionElement.id.replace("shopify-section-", "");
  }

  return "header";
}

function loadStylesheet(url, id) {
  if (!url || loadedStylesheets.has(url)) {
    return Promise.resolve();
  }

  if (document.getElementById(id)) {
    loadedStylesheets.add(url);
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = url;
    link.onload = () => {
      loadedStylesheets.add(url);
      resolve();
    };
    link.onerror = reject;
    document.head.appendChild(link);
  });
}

function loadScript(url, id) {
  if (!url || loadedScripts.has(url)) {
    return Promise.resolve();
  }

  if (document.getElementById(id)) {
    loadedScripts.add(url);
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.id = id;
    el.src = url;
    el.defer = true;
    el.onload = () => {
      loadedScripts.add(url);
      resolve();
    };
    el.onerror = reject;
    document.head.appendChild(el);
  });
}

function loadMegaMenuCss() {
  return loadStylesheet(MEGA_MENU_CSS_URL, "mega-menu-sails-v3-css");
}

function loadMegaMenuJs() {
  return loadScript(MEGA_MENU_JS_URL, "mega-menu-sails-v3-js");
}

function loadMobileMenuCss() {
  return loadStylesheet(MOBILE_MENU_CSS_URL, "mobile-menu-v3-css");
}

async function loadDeferredMegaMenu(menu) {
  if (menu.dataset.deferredLoaded === "true") {
    return;
  }

  if (loadingMenus.has(menu.id)) {
    return loadingMenus.get(menu.id);
  }

  const promise = (async () => {
    await Promise.all([loadMegaMenuCss(), loadMegaMenuJs()]);

    const sectionId = extractSectionId(menu.closest(".shopify-section") || menu);
    const response = await fetch(`${Shopify.routes.root}?section_id=${sectionId}&deferred=0`, {
      priority: "high"
    });
    const doc = new DOMParser().parseFromString(await response.text(), "text/html");
    const fetchedMenu = doc.getElementById(menu.id);

    if (!fetchedMenu) {
      throw new Error(`Deferred mega menu not found: ${menu.id}`);
    }

    menu.innerHTML = fetchedMenu.innerHTML;

    for (const { name, value } of fetchedMenu.attributes) {
      if (name === "id" || name === "data-deferred-mega-menu") {
        continue;
      }
      menu.setAttribute(name, value);
    }

    menu.removeAttribute("data-deferred-mega-menu");
    menu.dataset.deferredLoaded = "true";
    menu.classList.remove("mega-menu-sails--deferred");

    document.dispatchEvent(new CustomEvent("loftsails:menu:loaded"));
  })();

  loadingMenus.set(menu.id, promise);

  try {
    await promise;
  } finally {
    loadingMenus.delete(menu.id);
  }
}

function primeDeferredMegaMenuLoad(trigger) {
  const menuId = trigger.getAttribute("aria-controls");
  if (!menuId) {
    return;
  }

  const menu = document.getElementById(menuId);
  if (menu?.hasAttribute("data-deferred-mega-menu")) {
    menu._deferredLoadPromise = loadDeferredMegaMenu(menu);
  }
}

function handleDeferredMegaMenuOpen(menu) {
  if (!menu.hasAttribute("data-deferred-mega-menu")) {
    return;
  }

  menu.removeAttribute("open");
  menu._deferredControls?.forEach((control) => control.setAttribute("aria-expanded", "false"));

  const reopen = () => {
    menu.show({ animate: false });
  };

  (menu._deferredLoadPromise || loadDeferredMegaMenu(menu))
    .then(reopen)
    .catch(() => {
      menu.classList.add("mega-menu-sails--deferred-error");
    });
}

function observeDeferredMegaMenu(menu) {
  menu._deferredControls = Array.from(
    menu.getRootNode().querySelectorAll(`[aria-controls="${menu.id}"]`)
  );

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.attributeName !== "open" || !menu.hasAttribute("open")) {
        continue;
      }

      handleDeferredMegaMenuOpen(menu);
    }
  });

  observer.observe(menu, { attributes: true, attributeFilter: ["open"] });
}

function initDeferredMegaMenus() {
  document.querySelectorAll("header-mega-menu[data-deferred-mega-menu]").forEach((menu) => {
    if (menu.dataset.deferredObserved === "true") {
      return;
    }

    menu.dataset.deferredObserved = "true";
    observeDeferredMegaMenu(menu);
  });
}

function primeDeferredMegaMenuFromEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }
  const trigger = target.closest("[aria-controls]");
  if (trigger) {
    primeDeferredMegaMenuLoad(trigger);
  }
}

document.addEventListener("pointerenter", primeDeferredMegaMenuFromEvent, true);
document.addEventListener("focusin", primeDeferredMegaMenuFromEvent, true);
document.addEventListener("mousedown", primeDeferredMegaMenuFromEvent, true);

document.addEventListener(
  "click",
  (event) => {
    const trigger = event.target.closest("[data-mobile-menu-v3-trigger]");
    if (!trigger) {
      return;
    }

    loadMobileMenuCss();
  },
  true
);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initDeferredMegaMenus);
} else {
  initDeferredMegaMenus();
}

document.addEventListener("shopify:section:load", initDeferredMegaMenus);
